import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Leaf, MessageSquare, Shield, MoonStar } from 'lucide-react';
import { setLang } from '../lib/i18n';
import { getOrAssignVariant, getOrAssignAnonId, track } from '../lib/ab';

// Two creative directions tested on /en. Variant is sticky per visitor.
//   A — "Quiet companion" (loss-aversion / no-one-else)
//   B — "Anti-therapy"   (no waitlist, no copay, just texting)
const COPY = {
    A: {
        eyebrow: 'Quiet on purpose.',
        hero: "Some things you can't say out loud yet.",
        sub: "A place to talk when there's no one else. No account, no notifications, no algorithm trying to fix you. Just a conversation that listens.",
        ctaPrimary: 'Start talking — 3 days free',
        ctaSecondary: 'Or just open the door',
        finePrint: 'No email, no password. Anonymous by default.',
        pricingHeadline: '$7 a month. That\u2019s it.',
        pricingSub: 'Three days free. After that, $7/month for 30 minutes a day, or $14/month for unlimited. Cancel any time, from inside the app, with two clicks.',
        pricingCta: 'Start your 3 free days',
    },
    B: {
        eyebrow: 'Not therapy. Not a chatbot.',
        hero: "You don't need a 50-minute session. Just someone to text.",
        sub: 'Therapy has a 6-week waitlist and a $200 copay. Stillroom doesn\u2019t. Anonymous, honest, quiet \u2014 for the 11 p.m. thoughts you don\u2019t say to anyone else.',
        ctaPrimary: 'Text Stillroom — 3 days free',
        ctaSecondary: 'No email. No copay.',
        finePrint: 'Anonymous by default. Cancel in two taps.',
        pricingHeadline: 'Cheaper than one copay.',
        pricingSub: 'Three days free, then $7/month for 30 minutes a day or $14/month unlimited. No waitlist, no intake form, no commitment. Cancel any time.',
        pricingCta: 'Start texting — free for 3 days',
    },
};

export default function EnLanding() {
    const navigate = useNavigate();
    const [variant] = React.useState(getOrAssignVariant);
    const copy = COPY[variant] || COPY.A;
    const viewFiredRef = React.useRef(false);

    React.useEffect(() => {
        setLang('en');
        getOrAssignAnonId();
        if (!viewFiredRef.current) {
            viewFiredRef.current = true;
            track('landing_view');
        }
    }, []);

    const startTalking = (cta) => {
        track('cta_click', { cta });
        navigate('/auth');
    };

    return (
        <div className="min-h-screen bg-[#F5F0E6] text-[#1F1F1F]" data-testid="en-landing" data-ab-variant={variant}>
            {/* Header */}
            <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center">
                        <Leaf className="w-4 h-4 text-bone"/>
                    </div>
                    <span className="font-display font-bold tracking-tight text-lg">Stillroom</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                    <Link to="/?lang=no" className="text-inkSoft hover:text-ink hidden sm:inline" data-testid="en-to-no-link" title="Norsk versjon">🇳🇴 Norsk</Link>
                    <Link to="/auth" className="text-inkSoft hover:text-ink" data-testid="en-signin-link">Sign in</Link>
                    <Button onClick={() => startTalking('header')} className="rounded-full bg-[#1F1F1F] hover:bg-black text-[#F5F0E6] text-sm h-9" data-testid="en-cta-header">
                        {variant === 'B' ? 'Start texting' : 'Start talking'}
                    </Button>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-3xl mx-auto px-6 pt-12 pb-20 md:pt-20 md:pb-32">
                <p className="text-xs uppercase tracking-[0.2em] text-inkSoft mb-6">{copy.eyebrow}</p>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="en-hero">
                    {copy.hero}
                </h1>
                <p className="mt-6 text-lg text-inkSoft leading-relaxed max-w-xl">
                    {copy.sub}
                </p>
                <div className="mt-10 flex flex-col sm:flex-row gap-3">
                    <Button onClick={() => startTalking('hero_primary')} className="rounded-full bg-[#1F1F1F] hover:bg-black text-[#F5F0E6] h-12 px-8 text-base" data-testid="en-cta-hero">
                        {copy.ctaPrimary}
                    </Button>
                    <Button onClick={() => startTalking('hero_secondary')} variant="outline" className="rounded-full border-[#1F1F1F]/20 hover:bg-[#1F1F1F]/5 h-12 px-8 text-base" data-testid="en-cta-anon">
                        {copy.ctaSecondary}
                    </Button>
                </div>
                <p className="mt-4 text-xs text-inkSoft">{copy.finePrint}</p>
            </section>

            {/* The voice */}
            <section className="bg-white border-y border-sand">
                <div className="max-w-3xl mx-auto px-6 py-20">
                    <p className="text-xs uppercase tracking-[0.2em] text-inkSoft mb-4">The voice</p>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
                        Honest. Short. Never performative.
                    </h2>
                    <div className="mt-10 grid md:grid-cols-2 gap-x-12 gap-y-8 text-sm leading-relaxed">
                        <div>
                            <p className="font-medium text-ink">Names something specific.</p>
                            <p className="text-inkSoft mt-1">No &quot;I&apos;m so sorry you&apos;re feeling this way.&quot; You said something. We notice it.</p>
                        </div>
                        <div>
                            <p className="font-medium text-ink">One question at a time.</p>
                            <p className="text-inkSoft mt-1">Concrete, not therapeutic. &quot;What were you doing right before it started?&quot;</p>
                        </div>
                        <div>
                            <p className="font-medium text-ink">No emoji. No bullet lists. No tips.</p>
                            <p className="text-inkSoft mt-1">If you wanted a self-help book, you&apos;d have one. This is a conversation.</p>
                        </div>
                        <div>
                            <p className="font-medium text-ink">Holds silence.</p>
                            <p className="text-inkSoft mt-1">If you go quiet, we don&apos;t fill the air. We wait, the way a friend would.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Sample exchange */}
            <section className="max-w-3xl mx-auto px-6 py-20">
                <p className="text-xs uppercase tracking-[0.2em] text-inkSoft mb-4">An example</p>
                <div className="rounded-3xl border border-sand bg-white p-8 space-y-5 shadow-sm">
                    <div className="flex gap-3">
                        <div className="text-xs uppercase tracking-wider text-inkSoft shrink-0 w-12">You</div>
                        <p className="text-ink leading-relaxed">I don&apos;t even know why I&apos;m crying. I just feel really off today.</p>
                    </div>
                    <div className="h-px bg-sand"></div>
                    <div className="flex gap-3">
                        <div className="text-xs uppercase tracking-wider text-sage-dark shrink-0 w-12 font-medium">Stillroom</div>
                        <p className="text-ink leading-relaxed italic">
                            You don&apos;t have to know why yet. Some days the body cries before the mind catches up. What were you doing right before it started?
                        </p>
                    </div>
                </div>
                <p className="text-xs text-inkSoft mt-4 text-center">No diagnosis. No tips. Just a question that takes you closer.</p>
            </section>

            {/* Why this is different */}
            <section className="bg-[#1F1F1F] text-[#F5F0E6]">
                <div className="max-w-3xl mx-auto px-6 py-24">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#F5F0E6]/60 mb-6">Why this is different</p>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                        Built in Scandinavia. Designed to be quiet about it.
                    </h2>
                    <p className="mt-6 text-[#F5F0E6]/80 leading-relaxed text-lg max-w-2xl">
                        Most American wellness apps want to fix you in 30 days. We don&apos;t. We come from a culture that values directness over enthusiasm, recognition over validation, and silence over noise. Stillroom is a single, calm voice — not a chatbot trying to sound like a therapist.
                    </p>
                    <div className="mt-12 grid sm:grid-cols-3 gap-6">
                        <Pillar icon={MessageSquare} title="A conversation, not therapy" body="Anonymous. Directly honest. Not clinical."/>
                        <Pillar icon={Shield} title="Yours alone" body="Nothing saved unless you ask. Delete anytime."/>
                        <Pillar icon={MoonStar} title="Always open" body="3 a.m. or Sunday afternoon. Same voice."/>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="max-w-3xl mx-auto px-6 py-20">
                <p className="text-xs uppercase tracking-[0.2em] text-inkSoft mb-4">If it helps, you can keep it</p>
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{copy.pricingHeadline}</h2>
                <p className="mt-4 text-inkSoft text-lg leading-relaxed max-w-xl">
                    {copy.pricingSub}
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <Button onClick={() => startTalking('pricing')} className="rounded-full bg-[#1F1F1F] hover:bg-black text-[#F5F0E6] h-12 px-8 text-base" data-testid="en-cta-pricing">
                        {copy.pricingCta}
                    </Button>
                </div>
            </section>

            {/* Crisis note */}
            <section className="border-t border-sand">
                <div className="max-w-3xl mx-auto px-6 py-12 text-sm text-inkSoft leading-relaxed">
                    <p className="font-medium text-ink mb-2">A note about safety.</p>
                    <p>
                        Stillroom is not a healthcare service and is not a replacement for therapy. If you&apos;re in crisis or thinking about hurting yourself, please reach out to{' '}
                        <a href="tel:988" className="text-ink underline" data-testid="en-988">988 (Suicide &amp; Crisis Lifeline)</a> — call or text, free and confidential, 24/7.
                        Or text <strong>HOME</strong> to <strong>741741</strong>.
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-sand">
                <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-inkSoft">
                    <p>© {new Date().getFullYear()} Stillroom. All rights reserved.</p>
                    <div className="flex items-center gap-4">
                        <Link to="/personvern" className="hover:text-ink" data-testid="en-footer-privacy">Privacy</Link>
                        <Link to="/vilkar" className="hover:text-ink" data-testid="en-footer-terms">Terms</Link>
                        <span>AI assistant — you are talking to AI, not a human.</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function Pillar({ icon: Icon, title, body }) {
    return (
        <div>
            <Icon className="w-5 h-5 text-[#F5F0E6]/80"/>
            <p className="font-medium mt-3">{title}</p>
            <p className="text-[#F5F0E6]/70 text-sm mt-1 leading-relaxed">{body}</p>
        </div>
    );
}
